/**
 * @file        src/lib/paxSectorDetail.js
 * @description The two per-sector, per-passenger answers the admin flight
 *              documents all need: WHICH ticket number was issued for this
 *              sector, and WHAT this traveller holds on it beyond the bare
 *              ticket. Both were duplicated between the Booking Details drawer
 *              and the e-ticket, and both were WRONG in one of the two copies —
 *              which is the whole argument for one definition.
 * @author      Kinetika Labs
 * @created     2026-09-23
 * @changelog
 *   - 2026-09-23 Claude — Initial. Extracted from BookingDetailsDrawer +
 *                          TicketDoc, fixing the duplicate-add-on defect
 *                          described on confirmedAddonLabels below.
 */

/**
 * The airline ticket number FOR ONE SECTOR. One is issued per sector, so a
 * document must print that sector's own number — `ticketNumber` is a SUMMARY
 * field (every distinct number joined with commas) and rendering it inside a
 * per-sector loop shows the whole list on every row. Prefer the route-keyed
 * map, fall back to positional order, then the single legacy field (which an
 * admin may have corrected by hand via PATCH /bookings/:id for a booking
 * ticketed offline, or in the sandbox, where the supplier has none of its own).
 */
export function ticketNoOf(pax, sector, sectorIdx) {
  return pax?.ticketByRoute?.[sector?.route]
    || pax?.tickets?.[sectorIdx]
    || (pax?.tickets?.length ? '' : pax?.ticketNumber)
    || '';
}

const addonKey = (x) => `${String(x?.type || '')}|${String(x?.code ?? '').toUpperCase()}`;
const sameRoute = (x, route) => String(x?.route || '') === String(route || '');
const decorate = (type, label) => (/seat/i.test(String(type || '')) ? `Seat ${label}` : String(label));

/**
 * What the SUPPLIER provisioned for ONE traveller on ONE sector, as readable
 * labels. Gated on `confirmed` — what the airline actually put on the ticket —
 * never on the raw selection, so an add-on the airline dropped is not printed as
 * delivered (payment_reconciliation owns that refund).
 *
 * THE ATTRIBUTION PROBLEM, which is why this is not a filter. `confirmed_addons`
 * is built by booking_backend's extractConfirmedSsr, which flattens EVERY
 * traveller's SSR into one list of {type, route, code, amount} and discards
 * which traveller each row came from — deliberately, because reconcileSsr only
 * needs a multiset diff. So a 2-passenger booking where BOTH travellers chose
 * the same meal on the same sector produces two identical, indistinguishable
 * rows.
 *
 * Both call sites used to re-derive ownership by asking "does ANY selection with
 * this type+route+code carry my paxIndex?" — which is true of both rows for both
 * travellers, so each passenger's cell printed "Baby Meal, Baby Meal". It only
 * ever worked when the two travellers picked DIFFERENT codes.
 *
 * The fix is a CONSUMING pair-up, not a test: within one type+route+code group,
 * hand the confirmed rows out to the paid-for selections in passenger order, one
 * each. A traveller therefore sees a label once per add-on they actually bought,
 * and when the airline provisioned fewer than were paid for the shortfall falls
 * off the end rather than being duplicated onto everyone.
 *
 * @param {object[]} confirmed  booking_details.confirmedAddons (supplier truth)
 * @param {object[]} selected   booking_details.selectedAddons (carries paxIndex)
 * @param {string}   route      "FROM-TO"
 * @param {number}   paxIndex   0-based traveller position
 * @returns {string[]}
 */
export function confirmedAddonLabels({ confirmed, selected, route, paxIndex }) {
  const conf = (confirmed || []).filter((c) => sameRoute(c, route));
  if (!conf.length) return [];
  const picksAll = (selected || []).filter((s) => sameRoute(s, route));

  // One entry per distinct add-on, carrying how many of it the airline issued.
  const groups = new Map();
  for (const c of conf) {
    const k = addonKey(c);
    const g = groups.get(k) || { count: 0, type: c?.type, code: c?.code };
    g.count += 1;
    groups.set(k, g);
  }

  const out = [];
  for (const [k, g] of groups) {
    const picks = picksAll.filter((s) => addonKey(s) === k);
    // Stable by passenger, so "who loses the one the airline dropped" is a
    // consistent answer and not whatever order the arrays happen to be in.
    const tagged = picks
      .filter((s) => s.paxIndex != null)
      .sort((a, b) => Number(a.paxIndex) - Number(b.paxIndex));
    const labelOf = (s) => s?.label || picks[0]?.label || g.code || '';

    if (tagged.length) {
      for (const s of tagged.slice(0, g.count)) {
        if (Number(s.paxIndex) !== Number(paxIndex)) continue;
        const label = labelOf(s);
        if (label) out.push(decorate(s.type || g.type, label));
      }
      continue;
    }
    // Nothing carries a passenger index (a booking made before checkout started
    // tagging its selections, or a supplier row we sold no selection for), so
    // ownership is genuinely unknowable. Print it ONCE against this traveller
    // rather than once per confirmed row — repeating it is the defect above,
    // and it is the one outcome that is certainly wrong.
    const label = labelOf(picks[0]);
    if (label) out.push(decorate(g.type, label));
  }
  return out;
}

/**
 * The per-sector SSR the supplier itself reported for this traveller
 * (`passengers[].ssrByRoute`, merged on by liveFlightBooking.service). This is
 * the UNAMBIGUOUS answer — it is built per traveller, so it needs none of the
 * attribution above — and is preferred wherever it exists.
 */
export function supplierSsrLabels(pax, route) {
  const ssr = (pax?.ssrByRoute || []).find((s) => String(s?.route || '') === String(route || ''));
  if (!ssr) return null;
  const parts = [];
  if (ssr.meal) parts.push(String(ssr.meal));
  if (ssr.seat) parts.push(`Seat ${ssr.seat}`);
  if (ssr.baggage) parts.push(String(ssr.baggage));
  return parts;
}
